import type { RotatingPlayer, RotatingSet } from '@tark/shared/lib/rotatingSets';

const sideKey = (players: RotatingPlayer[]) => players.map(p => p.kind === 'USER' ? `U:${p.userId}`
  : p.kind === 'CHILD' ? `C:${p.athleteId}` : `G:${p.displayName.trim().toLowerCase()}`).sort().join('|');
export function validTiebreak(a: number | null, b: number | null, target = 7) {
  if (a == null || b == null || !Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0) return false;
  const hi = Math.max(a, b), lo = Math.min(a, b);
  return hi <= 99 && ((hi === target && lo <= target - 2) || (hi > target && hi - lo === 2));
}
export function scoreError(set: RotatingSet): string | null {
  const a = set.gamesA, b = set.gamesB;
  if (a == null || b == null || !Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0 || a > 99 || b > 99)
    return 'Enter both scores as whole numbers from 0 to 99.';
  const hi = Math.max(a,b), lo = Math.min(a,b);
  const valid = set.format === 'SIX_GAME' ? (hi === 6 && lo <= 4) || (hi === 7 && (lo === 5 || lo === 6))
    : set.format === 'FOUR_GAME' ? hi === 4 && lo <= 3
    : set.format === 'EIGHT_GAME' ? (hi === 8 && lo <= 6) || (hi === 9 && (lo === 7 || lo === 8))
    : set.format === 'TB7' ? validTiebreak(a,b,7)
    : set.format === 'TB10' ? validTiebreak(a,b,10)
    : set.format === 'EXT_BY2' ? hi <= 30 && hi - lo >= 2 : false;
  if (!valid) return 'The score must complete the selected set format.';
  if (set.tiebreakA != null || set.tiebreakB != null) {
    if (set.format !== 'SIX_GAME' || hi !== 7 || lo !== 6 || !validTiebreak(set.tiebreakA,set.tiebreakB)
      || (a > b) !== ((set.tiebreakA ?? 0) > (set.tiebreakB ?? 0)))
      return 'Enter both tiebreak scores (first to 7, win by 2), with the same winner as the 7–6 set.';
  }
  return null;
}

/** Only attach a final, adjacent standalone tiebreak; never renumber other saved matches. */
export function attachFinalTiebreak(sets: RotatingSet[]): RotatingSet[] | null {
  if (sets.length < 2) return null;
  const previous = sets[sets.length - 2], tb = sets[sets.length - 1];
  if (tb.format !== 'TB7' || previous.format !== 'SIX_GAME' || previous.tiebreakA != null || previous.tiebreakB != null
    || Math.max(previous.gamesA ?? 0,previous.gamesB ?? 0) !== 7 || Math.min(previous.gamesA ?? 0,previous.gamesB ?? 0) !== 6) return null;
  const same = sideKey(previous.sideA) === sideKey(tb.sideA) && sideKey(previous.sideB) === sideKey(tb.sideB);
  const reversed = sideKey(previous.sideA) === sideKey(tb.sideB) && sideKey(previous.sideB) === sideKey(tb.sideA);
  if (!same && !reversed) return null;
  const joined = { ...previous, tiebreakA: same ? tb.gamesA : tb.gamesB, tiebreakB: same ? tb.gamesB : tb.gamesA };
  if (scoreError(joined)) return null;
  return [...sets.slice(0,-2),joined];
}
